import { useState, useEffect } from 'react';
import { Button, Form, Icon, Segment, Header, Message, Progress, Dimmer, Loader, Checkbox } from 'semantic-ui-react';
import SusuModal from '../common/SusuModal';
import SusuToggle from '../common/SusuToggle';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClothingItems } from '../../hooks/useClothingItems';
import {
  useRemoveBgFromUploadMutation,
  useRemoveBgFromUrlMutation,
  useToggleClothingBackgroundMutation,
  useRemoveClothingBackgroundMutation,
  useUpdateClothingWithUploadMutation,
  useCheckExistingClothingMutation
} from '../../redux/apis/endpoints/closetApi';
import { processFileForUpload } from '../../utils/imageCompression';
import DeletionWarningModal from '../deletion-warning-modal/DeletionWarningModal';
import ConfirmModal from '../common/ConfirmModal';
import PersonImageEditor from '../person-image-editor/PersonImageEditor';
import ProcessingModal from '../common/ProcessingModal';
import useVTODeletion from '../../hooks/useVTODeletion';
import { useCredits } from '../../hooks/useCredits';
import VTOLoginConfirmationPortal from '../virtual-try-on/VTOLoginConfirmationPortal';
import './UploadClothes.css';

function UploadClothes() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [clothesName, setClothesName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [itemId, setItemId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // VTO deletion warning state
  const [showDeletionWarning, setShowDeletionWarning] = useState(false);
  const [vtoCount, setVtoCount] = useState(0);
  const [deletionError, setDeletionError] = useState(null);

  // Crop editor state
  const [showCropEditor, setShowCropEditor] = useState(false);

  // Background removal processing modal state
  const [showBgRemovalProcessing, setShowBgRemovalProcessing] = useState(false);

  // Login prompt modal state
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Existing clothing check state
  const [existingClothing, setExistingClothing] = useState(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Credits hook
  const { balance, hasCredits, refetch: refetchCredits } = useCredits();

  // Use RTK Query hooks
  const {
    uploadClothing,
    updateClothing,
    deleteClothing,
    getItemById,
    isLoading,
    isUploading,
    isUpdating,
    isDeleting
  } = useClothingItems();

  // Background removal mutations
  const [removeBgFromUpload, { isLoading: isRemovingBgUpload }] = useRemoveBgFromUploadMutation();
  const [removeBgFromUrl, { isLoading: isRemovingBgUrl }] = useRemoveBgFromUrlMutation();
  const [toggleClothingBackground, { isLoading: isTogglingBg }] = useToggleClothingBackgroundMutation();
  const [removeClothingBackground, { isLoading: isRemovingClothingBg }] = useRemoveClothingBackgroundMutation();
  const [updateClothingWithUpload, { isLoading: isUpdatingWithUpload }] = useUpdateClothingWithUploadMutation();
  const [checkExistingClothing] = useCheckExistingClothingMutation();

  // Track if item has removed background (for toggle behavior in edit mode)
  const [hasRemovedBg, setHasRemovedBg] = useState(false);
  const [useRemovedImage, setUseRemovedImage] = useState(false);
  const [originalIsPublic, setOriginalIsPublic] = useState(false);

  // Track if initial data has been loaded (to prevent re-fetching from overwriting local changes)
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // VTO deletion hook
  const { checkVTOCount, deleteWithCascade, isDeleting: isDeletingWithCascade } = useVTODeletion();

  const loading = isUploading || isUpdating || isDeleting || isRemovingBgUpload || isRemovingBgUrl || isDeletingWithCascade || isTogglingBg || isRemovingClothingBg || isUpdatingWithUpload || isCheckingExisting;

  const hasChanges = isEditMode ? (
    file !== null ||
    clothesName !== '' || // Actually need to compare with original name
    isPublic !== originalIsPublic
  ) : true;

  useEffect(() => {
    // Skip if we've already loaded initial data
    if (initialDataLoaded) return;

    // Check if user is logged in
    const isLoggedIn = !!localStorage.getItem('token');

    // Check if we're in edit mode
    if (location.pathname === '/edit-clothes') {
      setIsEditMode(true);
      const params = new URLSearchParams(location.search);
      const id = params.get('id');
      setItemId(id);

      if (!isLoggedIn) {
        // Get item from localStorage for guest mode
        const guestClothes = JSON.parse(localStorage.getItem('guestClothes') || '[]');
        const item = guestClothes.find(item => item.id.toString() === id);
        if (item) {
          setClothesName(item.clothesName || '');
          setPreview(item.image);
          setIsPublic(item.isPublic || false);
          setOriginalIsPublic(item.isPublic || false);
          setInitialDataLoaded(true);
        }
      } else {
        // Fetch from backend for logged-in users using RTK Query
        const item = getItemById(id);
        if (item) {
          setClothesName(item.clothesName || '');
          // Handle both string URLs and image objects
          const imageUrl = typeof item.image === 'string'
            ? item.image
            : item.image?.url || item.image;

          // Store removed bg state
          const removedUrl = item.removedBgImage?.url;
          setHasRemovedBg(!!removedUrl);
          setUseRemovedImage(item.useRemovedImage === true);

          // Show the appropriate image based on useRemovedImage flag
          if (item.useRemovedImage && removedUrl) {
            setPreview(removedUrl);
          } else {
            setPreview(imageUrl);
          }
          setIsPublic(item.isPublic || false);
          setOriginalIsPublic(item.isPublic || false);
          setInitialDataLoaded(true);
        } else if (!isLoading) {
          setError('Item not found');
        }
      }
    }
  }, [location.pathname, location.search, getItemById, isLoading, initialDataLoaded]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      try {
        // Process file: convert HEIC if needed, compress if > 5MB
        const processedFile = await processFileForUpload(selectedFile, {
          maxWidth: 2048,
          maxHeight: 2048,
          targetSizeMB: 5,
          thresholdMB: 5,
          minQuality: 0.5,
          maxQuality: 0.9
        });

        setFile(processedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(processedFile);

        // Check if clothing already exists (only for logged-in users, not in edit mode)
        const isLoggedIn = !!localStorage.getItem('token');
        if (isLoggedIn && !isEditMode) {
          setIsCheckingExisting(true);
          setExistingClothing(null);
          try {
            const result = await checkExistingClothing(processedFile).unwrap();
            if (result.exists && result.data) {
              setExistingClothing(result.data);
              setClothesName(result.data.clothesName || '');
            }
          } catch (err) {
            console.error('Failed to check existing clothing:', err);
          } finally {
            setIsCheckingExisting(false);
          }
        }
      } catch (err) {
        console.error('File processing failed:', err);
        setError('Failed to process image. Please try another format.');
      }
    }
  };

  const handleGuestSubmit = async () => {
    if (!file && !isEditMode) {
      setError('Please provide an image for your clothing item');
      return;
    }

    const guestClothes = JSON.parse(localStorage.getItem('guestClothes') || '[]');

    if (isEditMode) {
      // Update existing item in localStorage
      const updatedClothes = guestClothes.map(item => {
        if (item.id.toString() === itemId) {
          return {
            ...item,
            clothesName,
            image: preview
          };
        }
        return item;
      });
      localStorage.setItem('guestClothes', JSON.stringify(updatedClothes));
      console.log('✅ Updated guest item in localStorage:', updatedClothes.length, 'items');
    } else {
      // Add new item to localStorage
      const newItem = {
        id: Date.now(),
        clothesName,
        image: preview
      };
      guestClothes.push(newItem);
      localStorage.setItem('guestClothes', JSON.stringify(guestClothes));
      console.log('✅ Added new guest item to localStorage:', guestClothes.length, 'items total');
    }

    navigate('/my-closet');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUploadProgress(0);

    if (!file && !preview && !isEditMode) {
      setError('Please provide an image for your clothing item');
      return;
    }

    // Check if user is logged in
    const isLoggedIn = !!localStorage.getItem('token');

    if (!isLoggedIn) {
      // Save to localStorage for non-logged-in users
      await handleGuestSubmit();
      return;
    }

    // Logged-in users: save to backend
    try {
      setUploadProgress(20);

      if (isEditMode) {
        setUploadProgress(60);

        // If there's a new file (from cropping or changing image), use updateWithUpload
        if (file) {
          await updateClothingWithUpload({
            id: itemId,
            file,
            clothesName,
            isPublic
          }).unwrap();
        } else {
          // Just update metadata
          await updateClothing({
            id: itemId,
            clothesName,
            isPublic
          });
        }
        setUploadProgress(100);
      } else {
        // Create new item using RTK Query with file upload
        if (!file) {
          setError('Please select an image file');
          return;
        }

        setUploadProgress(60);
        await uploadClothing({
          file,
          clothesName,
          isPublic
        });
        setUploadProgress(100);
      }

      // Success feedback
      setTimeout(() => {
        navigate('/my-closet');
      }, 500);

    } catch (err) {
      setError(`Failed to ${isEditMode ? 'update' : 'upload'} clothing item. Please try again.`);
      console.error('Error:', err);
      setUploadProgress(0);
    }
  };

  const handleDeleteClick = async () => {
    const isLoggedIn = !!localStorage.getItem('token');

    if (!isLoggedIn) {
      // Guest users - show normal delete modal
      setShowDeleteModal(true);
    } else {
      // Check VTO count for logged-in users
      try {
        const count = await checkVTOCount(itemId, 'clothing-item');
        if (count > 0) {
          // Show warning modal
          setVtoCount(count);
          setShowDeletionWarning(true);
        } else {
          // No VTO results, proceed with normal delete
          setShowDeleteModal(true);
        }
      } catch (error) {
        console.error('Error checking VTO count:', error);
        // On error, show normal delete modal
        setShowDeleteModal(true);
      }
    }
  };

  const handleDelete = async () => {
    const isLoggedIn = !!localStorage.getItem('token');

    if (!isLoggedIn) {
      // Delete from localStorage for non-logged-in users
      const guestClothes = JSON.parse(localStorage.getItem('guestClothes') || '[]');
      const updatedClothes = guestClothes.filter(item => item.id.toString() !== itemId);
      localStorage.setItem('guestClothes', JSON.stringify(updatedClothes));
      setShowDeleteModal(false);
      navigate('/my-closet');
    } else {
      // Delete from backend using RTK Query
      try {
        await deleteClothing(itemId);
        setShowDeleteModal(false);
        navigate('/my-closet');
      } catch (err) {
        setError('Failed to delete item. Please try again.');
        console.error('Delete error:', err);
        setShowDeleteModal(false);
      }
    }
  };

  /**
   * Handle removing existing clothing item
   */
  const handleRemoveExisting = async () => {
    if (!existingClothing?.id) return;

    try {
      setError('');
      await deleteClothing(existingClothing.id);
      setExistingClothing(null);
      setClothesName('');
      // Keep the preview and file so user can re-upload
    } catch (err) {
      setError('Failed to remove existing item. Please try again.');
      console.error('Remove existing error:', err);
    }
  };

  const handleCancelDeletionWarning = () => {
    setShowDeletionWarning(false);
    setVtoCount(0);
    setDeletionError(null);
  };

  const handleConfirmCascadeDelete = async () => {
    try {
      setDeletionError(null);

      // Perform cascade delete
      await deleteWithCascade(itemId, 'clothing-item');

      // Close modal and navigate
      setShowDeletionWarning(false);
      navigate('/my-closet');

      console.log('✅ Cascade delete completed successfully');
    } catch (error) {
      console.error('❌ Cascade delete failed:', error);
      setDeletionError(error.message || 'Failed to delete clothing item and VTO results');
    }
  };

  const handleRemoveBackground = async () => {
    if (!preview) {
      setError('Please upload an image first.');
      return;
    }

    const isLoggedIn = !!localStorage.getItem('token');

    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    // For new background removal (not toggle), check credits
    const needsCredit = !(isEditMode && hasRemovedBg);
    if (needsCredit && !hasCredits) {
      setError('No credits remaining. Buy credits or invite friends to earn more.');
      return;
    }

    try {
      setError('');

      // In edit mode with existing item that has removed background - toggle (no credit needed)
      if (isEditMode && itemId && hasRemovedBg) {
        const result = await toggleClothingBackground(itemId).unwrap();
        const newUseRemoved = result.useRemovedImage;
        setUseRemovedImage(newUseRemoved);

        // Update preview to show the toggled image
        if (newUseRemoved && result.removedBgImage?.url) {
          setPreview(result.removedBgImage.url);
        } else if (result.image?.url) {
          setPreview(result.image.url);
        }
        return;
      }

      // Show processing modal for new background removal
      setShowBgRemovalProcessing(true);

      // In edit mode - use the new endpoint that saves to removedBgImage field (costs 1 credit)
      if (isEditMode && itemId) {
        const result = await removeClothingBackground(itemId).unwrap();
        if (result.removedBgImage?.url) {
          setHasRemovedBg(true);
          setUseRemovedImage(true);
          setPreview(result.removedBgImage.url);
        }
        // Refetch credits after successful removal
        refetchCredits?.();
        setShowBgRemovalProcessing(false);
        return;
      }

      // For new uploads (not yet saved), use the old behavior
      let result;
      if (file) {
        result = await removeBgFromUpload(file).unwrap();
      } else {
        result = await removeBgFromUrl(preview).unwrap();
      }

      if (result && result.url) {
        setPreview(result.url);
        setFile(null);
      } else {
        setError('Failed to remove background. Please try again.');
      }
      setShowBgRemovalProcessing(false);
    } catch (err) {
      setShowBgRemovalProcessing(false);
      setError(err?.data?.message || err?.message || 'Failed to remove background. Please try again.');
      console.error('Remove background error:', err);
    }
  };

  const handleCropSave = async (blob) => {
    // Convert blob to file and update preview
    const croppedFile = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
    setFile(croppedFile);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
      setShowCropEditor(false);
    };
    reader.readAsDataURL(blob);
  };

  // Determine loading message
  const getLoadingMessage = () => {
    if (isRemovingBgUpload || isRemovingBgUrl || isRemovingClothingBg) return 'Removing background...';
    if (isTogglingBg) return 'Toggling background...';
    if (isUploading) return 'Uploading...';
    if (isUpdating || isUpdatingWithUpload) return 'Saving changes...';
    if (isDeleting || isDeletingWithCascade) return 'Deleting...';
    return 'Processing...';
  };

  return (
    <div className="upload-clothes-container">
      <Segment className="upload-segment">
        <Dimmer active={loading} inverted>
          <Loader size="large">{getLoadingMessage()}</Loader>
        </Dimmer>

        <div className="upload-header">
          <Icon
            name="arrow left"
            size="large"
            className="back-button"
            onClick={() => navigate('/my-closet')}
          />
          <Header as='h2' color='teal' className="upload-title">
            {isEditMode ? 'Edit Item' : 'Upload Clothing'}
          </Header>
        </div>

        <Form onSubmit={handleSubmit}>
          {/* Main Action Buttons at the top */}
          <div className="button-row" style={{ marginBottom: '15px' }}>
            <Button
              type='submit'
              color='teal'
              disabled={(!file && !preview && !isEditMode) || loading}
              className={isEditMode ? "half-width-button" : "full-width-button"}
            >
              <Icon name={isEditMode ? 'save' : 'upload'} />
              {isEditMode ? 'Save Changes' : 'Upload to My Closet'}
            </Button>

            {isEditMode && (
              <Button
                type="button"
                color="red"
                basic
                onClick={handleDeleteClick}
                disabled={loading}
                className="half-width-button"
              >
                <Icon name="trash" /> Delete
              </Button>
            )}
          </div>

          <div className="button-row">
            <Button
              as="label"
              htmlFor="file-input"
              type="button"
              basic
              color="yellow"
              className="half-width-button"
            >
              <Icon name='upload' /> {isEditMode ? 'Change Image' : 'Choose Image'}
            </Button>

            <Button
              type="button"
              color="teal"
              basic
              onClick={() => setShowCropEditor(true)}
              disabled={!preview || loading}
              className="half-width-button"
            >
              <Icon name='crop' /> Crop & Resize
            </Button>
          </div>

          <div className="button-row">
            <Button
              type="button"
              color="blue"
              basic
              onClick={handleRemoveBackground}
              disabled={!preview || loading}
              className="full-width-button"
            >
              <Icon name={isEditMode && hasRemovedBg ? (useRemovedImage ? 'undo' : 'eraser') : 'magic'} />
              {isEditMode && hasRemovedBg
                ? (useRemovedImage ? ' Restore Background' : ' Use Removed BG')
                : ' Remove Background'
              }
            </Button>
          </div>

          {/* Hidden file input */}
          <input
            id="file-input"
            type="file"
            hidden
            onChange={handleFileChange}
            accept="image/*,.heic"
          />

          {/* Progress indicator */}
          {loading && uploadProgress > 0 && (
            <Progress
              percent={uploadProgress}
              color="teal"
              progress
              indicating
              style={{ marginTop: '10px' }}
            />
          )}

          {/* Messages and feedback */}
          <div className="feedback-section" style={{ margin: '15px 0' }}>
            {/* Show if checking for existing clothing */}
            {isCheckingExisting && (
              <Message info>
                <Icon name="search" loading />
                Checking if this item already exists...
              </Message>
            )}

            {/* Show if clothing already exists */}
            {!isCheckingExisting && existingClothing && (
              <Message warning>
                <Message.Header>
                  <Icon name="warning sign" />
                  Already in Closet
                </Message.Header>
                <p>
                  This item is already in your closet as <strong>{existingClothing.clothesName || 'Unnamed Item'}</strong>.
                </p>
                <Button
                  color="red"
                  size="small"
                  onClick={handleRemoveExisting}
                  disabled={loading}
                >
                  <Icon name="trash" />
                  Remove from Closet
                </Button>
              </Message>
            )}

          {/* Share to Community Toggle */}
          <div style={{ margin: '1em 0', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name="globe" color={isPublic ? 'teal' : 'grey'} style={{ margin: 0 }} />
              <span style={{ fontSize: '13px', color: '#333', fontWeight: 500 }}>Share to Community</span>
            </div>
            <SusuToggle
              checked={isPublic}
              onChange={(checked) => setIsPublic(checked)}
              disabled={loading}
            />
          </div>

          {error && (
              <Message negative>
                <p>{error}</p>
              </Message>
            )}
          </div>

          {/* Image Preview at the bottom */}
          <div
            className="upload-preview-container"
            onClick={() => document.getElementById('file-input').click()}
            style={{ cursor: 'pointer', marginTop: '10px', position: 'relative' }}
          >
            {preview ? (
              <>
                <img
                  src={preview}
                  alt="Preview"
                  className="upload-preview"
                />
                {/* Public/Private Toggle overlay at the bottom of the image */}
                <div className="visibility-toggle-overlay" style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  padding: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderTop: '1px solid rgba(0,0,0,0.05)',
                  backdropFilter: 'blur(4px)'
                }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon
                      name={isPublic ? 'world' : 'lock'}
                      color={isPublic ? 'pink' : 'grey'}
                      style={{ margin: 0 }}
                    />
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: isPublic ? '#FF69B4' : '#666'
                    }}>
                      {isPublic ? 'Public - Share on Map' : 'Private - Closet Only'}
                    </span>
                  </div>
                  <SusuToggle
                    checked={isPublic}
                    onChange={(checked) => setIsPublic(checked)}
                  />
                </div>
              </>
            ) : (
              <div className="upload-placeholder">
                <Icon name="image outline" size="huge" color="grey" />
                <p>Click to upload image</p>
              </div>
            )}
          </div>
        </Form>
      </Segment>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Clothing Item"
        titleIconName="trash"
        titleIconColor="red"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
        loadingText="Deleting..."
      >
        <p>Are you sure you want to delete this item?</p>
      </ConfirmModal>

      {/* VTO Deletion Warning Modal */}
      <DeletionWarningModal
        isOpen={showDeletionWarning}
        onClose={handleCancelDeletionWarning}
        onConfirm={handleConfirmCascadeDelete}
        itemType="clothing-item"
        vtoCount={vtoCount}
        isDeleting={isDeletingWithCascade}
        error={deletionError}
      />

      {/* Crop & Resize Modal */}
      <SusuModal
        open={showCropEditor}
        onClose={() => setShowCropEditor(false)}
        size="large"
        closeIcon
      >
        <SusuModal.Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="crop" style={{ margin: 0 }} />
            <span>Crop & Resize Image</span>
          </div>
        </SusuModal.Header>
        <SusuModal.Content scrolling>
          {preview && (
            <PersonImageEditor
              imageUrl={preview}
              onSave={handleCropSave}
              onCancel={() => setShowCropEditor(false)}
            />
          )}
        </SusuModal.Content>
      </SusuModal>

      {/* Background Removal Processing Modal */}
      <ProcessingModal
        isOpen={showBgRemovalProcessing}
        title="Background Removal"
        message="Removing background from your clothing image..."
        icon="image"
        creditBalance={balance}
        creditsUsed={1}
        tip="Creating a clean cutout..."
      />

      {/* Login Required Modal */}
      <VTOLoginConfirmationPortal
        isOpen={showLoginModal}
        onLogin={() => {
          setShowLoginModal(false);
          window.location.reload();
        }}
        onCancel={() => setShowLoginModal(false)}
        onClose={() => setShowLoginModal(false)}
      />
    </div>
  );
}

export default UploadClothes;